import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  /** Nome da pessoa — vira o título da folha de baixo no celular. */
  titulo: string;
  /** Botão "⋮" que abriu o menu. O menu nasce colado nele. */
  ancora: RefObject<HTMLElement | null>;
  /** As ações (os botões já existentes), desenhadas fora do topo recortado. */
  children: ReactNode;
}

const LARGURA_MENU = 240;
const MARGEM = 8;
const ALTURA_ITEM = 52;
const ALTURA_CABECALHO = 60;

/**
 * Menu de ações do "⋮" que vive fora do topo recortado da folha.
 * No computador é um menu flutuante ancorado no botão; no celular é uma
 * folha de baixo com o nome da pessoa e as mesmas ações.
 */
export default function MenuAcoesFolha({ aberto, aoFechar, titulo, ancora, children }: Props) {
  const [celular, setCelular] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768,
  );
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Esc, toque fora e rolagem da folha fecham o menu. Redimensionar também.
  useEffect(() => {
    if (!aberto) return;
    const fechar = () => aoFechar();
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') aoFechar();
    };
    document.addEventListener('scroll', fechar, true);
    window.addEventListener('resize', fechar);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('scroll', fechar, true);
      window.removeEventListener('resize', fechar);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [aberto, aoFechar]);

  // Abriu: decide menu flutuante ou folha de baixo. Fechou: zera a posição medida.
  useEffect(() => {
    if (!aberto) {
      setPos(null);
      return;
    }
    setCelular(window.innerWidth < 768);
  }, [aberto]);

  // Mede o menu já desenhado e encaixa abaixo (ou acima) do botão, sem sair da tela.
  // Roda depois que o menu flutuante está montado (celular = false), então a
  // posição calculada não é apagada por nenhum outro efeito durante a abertura.
  useLayoutEffect(() => {
    if (!aberto || celular) return;
    const medir = () => {
      const el = menuRef.current;
      const botao = ancora.current;
      if (!el || !botao) return false;
      const r = botao.getBoundingClientRect();
      const largura = el.offsetWidth || LARGURA_MENU;
      const altura = el.offsetHeight || 2 * ALTURA_ITEM;
      const limiteDireita = Math.max(MARGEM, window.innerWidth - largura - MARGEM);
      const left = Math.min(Math.max(MARGEM, r.right - largura), limiteDireita);
      let top = r.bottom + MARGEM;
      if (top + altura > window.innerHeight - MARGEM) {
        top = r.top - MARGEM - altura;
        if (top < MARGEM) top = Math.max(MARGEM, window.innerHeight - altura - MARGEM);
      }
      setPos({ top, left });
      return true;
    };
    if (medir()) return;
    // Reserva: se o menu ainda não estiver no DOM neste ciclo, mede no próximo frame.
    let id = window.requestAnimationFrame(() => {
      if (medir()) return;
      id = window.requestAnimationFrame(() => {
        medir();
      });
    });
    return () => window.cancelAnimationFrame(id);
  }, [aberto, celular, ancora]);

  if (!aberto || typeof document === 'undefined') return null;

  // Posição de segurança: se a medição ainda não aconteceu, o menu já nasce
  // colado embaixo do botão (nunca em -9999, nunca invisível na tela).
  const posSegura = (() => {
    if (pos) return pos;
    const botao = ancora.current;
    if (!botao) return { top: MARGEM, left: MARGEM };
    const r = botao.getBoundingClientRect();
    const limite = Math.max(MARGEM, window.innerWidth - LARGURA_MENU - MARGEM);
    return { top: r.bottom + MARGEM, left: Math.min(Math.max(MARGEM, r.right - LARGURA_MENU), limite) };
  })();

  const corpo = celular
    ? createPortal(
        <div className="fixed inset-0 z-[60] flex flex-col justify-end">
          <div
            className="animate-menu-fade absolute inset-0 bg-black/40"
            onClick={aoFechar}
            aria-hidden
          />
          <div
            role="dialog"
            aria-label={`Ações de ${titulo}`}
            className="animate-menu-sobe relative w-full rounded-t-2xl bg-white pb-[env(safe-area-inset-bottom)]"
          >
            <div
              className="px-5 pb-2 pt-4"
              style={{ minHeight: ALTURA_CABECALHO - 8 }}
            >
              <p className="truncate text-[16px] font-bold text-foreground-950">{titulo}</p>
            </div>
            <div className="pb-1 [&>button]:!min-h-[52px] [&>button]:!px-5 [&>button]:!text-[15px]">
              {children}
            </div>
            <div className="mt-1 border-t border-background-200">
              <button
                type="button"
                onClick={aoFechar}
                className="flex min-h-[52px] w-full cursor-pointer items-center justify-center whitespace-nowrap text-[15px] font-semibold text-foreground-700 transition-colors hover:bg-background-100"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )
    : createPortal(
        <>
          <div className="fixed inset-0 z-[59]" onClick={aoFechar} aria-hidden />
          <div
            ref={menuRef}
            role="menu"
            aria-label={`Ações de ${titulo}`}
            style={{ top: posSegura.top, left: posSegura.left }}
            className="fixed z-[60] overflow-hidden rounded-2xl bg-white py-1 shadow-[0_6px_18px_rgba(20,25,60,.14)]"
          >
            {children}
          </div>
        </>,
        document.body,
      );

  return corpo;
}